//@ts-nocheck
import { memo, useEffect, useState, useRef } from "react";
import React from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Box,
  Chip,
  TextField,
} from '@mui/material';
import { Close, ExpandMore } from '@mui/icons-material';
import '../../components/commonComponent/MultiSelectWithButton/multiselect.scss';


interface Option {
  id: string;
  code?: string;
  label: string;
}


interface MultiSelectNewProps {
  options: Option[] | any;
  label?: string;
  selectedValues: string[];
  onApply: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  showLabelOnly?: boolean;
  selectAllLabel?: string;
  required?: boolean;
  hideSelectAllOnSearch?: boolean;
}


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MAX_MENU_HEIGHT = ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP;
const MENU_WIDTH = 250;


const MultiSelectNew: React.FC<MultiSelectNewProps> = ({
  options,
  label,
  selectedValues,
  onApply,
  disabled,
  placeholder,
  showLabelOnly = false,
  selectAllLabel,
  required,
  hideSelectAllOnSearch = false
}) => {
  const [tempSelectedOptions, setTempSelectedOptions] = useState<string[]>(selectedValues);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>(''); // For search input
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options); // Initially set filteredOptions to options
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Whenever options prop changes, reset filteredOptions to options
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    // Only set tempSelectedOptions to values that exist in current options to prevent MUI out-of-range warnings
    const validValues = selectedValues.filter(value => 
      options && options.some((option: Option) => option.code === value)
    );   
    setTempSelectedOptions(validValues);
  }, [selectedValues, options]);


  // Handles the search input and filters options accordingly
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (query.trim() === '') {
      setFilteredOptions(options);
    } else {
      const lowercasedQuery = query.toLowerCase();
      setFilteredOptions(
        options.filter((option) => {
          const optionId = String(option.id).toLowerCase();
          const optionLabel = option.label.toLowerCase();
          return optionLabel.includes(lowercasedQuery) || optionId.includes(lowercasedQuery);
        })
      );
    }
  };

  // Handles changes when selecting options from the dropdown
  const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target;
    const selectedValues = typeof value === 'string' ? value.split(',') : value as string[];
    setTempSelectedOptions((prev) => {
      const validSelections = selectedValues.filter((selected) => options.some(option => option.code === selected));
      return [...new Set([...prev, ...validSelections])];
    });
  };

  // Handles checkbox when selecting options 
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, optionCode: string) => {
    setTempSelectedOptions((prev) => {
      if (event.target.checked) {
        if (optionCode === selectAllLabel) {
          return options.map((option: any) => option?.code)
        } else {
          return [...new Set([...prev, optionCode])];
        }
      } else {
        if (optionCode === selectAllLabel) {
          return [];
        } else {
          return prev.filter((code) => code !== optionCode);
        }
      }
    });
  };

  // Clear selections and reset filters
  const handleClear = () => {
    setTempSelectedOptions([]);
    setSearchQuery(''); // Clear the search input as well
    setFilteredOptions(options); // Reset filtered options to the full options
    // Immediately apply the cleared state
    onApply([]);
  };


  // Apply selected options and close the dropdown
  const handleApply = () => {
    onApply(tempSelectedOptions);
    setOpen(false);
  };


  // Delete a selected tag (chip) from the selected options
  const handleDeleteTag = (tagToDelete: string) => {
    setTempSelectedOptions((prev) => {
      const updated = prev.filter((tag) => tag !== tagToDelete);
      // Immediately apply the change when a chip is deleted
      onApply(updated);
      return updated;
    });
  };

  // Handle dropdown close
  const handleClose = () => {
    setOpen(false);
  };

  // Handle dropdown open
  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };


  return (
    <FormControl fullWidth className='multi-select-wrapper multi-select-search'>
      {label && <label id="multi-select-label" className={`${disabled ? 'disabled' : ''}`}>{label} {required && <span>*</span>}</label>}
      <Select
        labelId="multi-select-label"
        multiple
        value={tempSelectedOptions.filter(value => 
          options && options.some((option: Option) => option.code === value)
        )}
        IconComponent={ExpandMore}
        onChange={handleSelectChange}
        onClose={handleClose}
        open={open}
        onOpen={handleOpen}
        disabled={disabled}
        placeholder={placeholder}
        renderValue={(selected) => (
          <Box>
            {selected.map((value) => {
              const option = options.find(opt => opt.code === value);
              return (
                <Chip
                  key={value}
                  label={
                    option
                      ? showLabelOnly
                        ? option.label
                        : `${option.label} (${option.code})`
                      : value
                  }
                  disabled={disabled}
                  onDelete={() => handleDeleteTag(value)}
                  deleteIcon={<Close onMouseDown={(event) => event.stopPropagation()} />}
                />
              );
            })}
          </Box>
        )}
        className='multi-select'
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: MAX_MENU_HEIGHT,
              width: MENU_WIDTH,
            },
            className: 'multi-select-menu multi-select-search-menu',
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          disableScrollLock: true,
          disablePortal: true,
        }}
      >
        {/* Search bar */}
        <Box p={1} className='search-wrapper'>
          <TextField
            inputRef={inputRef}
            fullWidth
            value={searchQuery} // This ensures the input box reflects what you type
            onChange={handleSearchChange}
            placeholder={`Type ${placeholder}`}
            variant="outlined"
            size="medium"
            disabled={disabled}
            autoComplete="off"
          />
        </Box>

        <Box className="list-wrap">
          {!!selectAllLabel && !!options?.length && !(hideSelectAllOnSearch && searchQuery.trim().length > 0) &&
            <MenuItem key={selectAllLabel} value={selectAllLabel} disableRipple>
              <ListItemText
                primary={selectAllLabel}
              />
              <Checkbox
                checked={tempSelectedOptions?.length === options?.length}
                onChange={(event) => handleCheckboxChange(event, selectAllLabel)} // Handle checkbox change
              />
            </MenuItem>
          }
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <MenuItem key={option.id} value={option.code} disableRipple>
                <ListItemText
                  primary={
                    showLabelOnly
                      ? option.label
                      : `${option.label} (${option.code})`
                  }
                />
                <Checkbox
                  checked={tempSelectedOptions.includes(option.code)}
                  onChange={(event) => handleCheckboxChange(event, option.code)} // Handle checkbox change
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No results found</MenuItem>
          )}
        </Box>


        <Box className="flex justify-content-between align-items-center list-footer">
          <Button
            onClick={handleClear}
            className='cancel-btn'
            disableRipple
            disabled={tempSelectedOptions.length === 0} // Disable cancel if no selection made
          >
            Cancel
          </Button>
          <Button onClick={handleApply} className='primary-medium-green-btn' disableRipple>
            Done
          </Button>
        </Box>
      </Select>
    </FormControl>
  );
};


export default memo(MultiSelectNew);